import * as React from 'react';
import { styled, Tooltip, Typography, TypographyTypeMap } from '@mui/material';

export interface EllipsisProps {
  children: string;
  className?: string;
  rows?: number;
  variant: TypographyTypeMap['props']['variant'];
  color?: TypographyTypeMap['props']['color'];
  style?: React.CSSProperties;
  testId?: string;
}
const EllipsedText = styled(Typography)<{ rowscount: number }>(({ rowscount }) => ({
  maxWidth: '100%',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  ...(rowscount > 1
    ? {
        display: '-webkit-box',
        WebkitLineClamp: rowscount,
        WebkitBoxOrient: 'vertical',
      }
    : {
        whiteSpace: 'nowrap',
      }),
}));

const Ellipsis: React.FC<EllipsisProps> = ({ style, children, color, variant, rows = 1, testId }) => {
  const textElementRef = React.useRef<HTMLSpanElement>() as React.MutableRefObject<HTMLSpanElement | null>;
  const [disableHover, setDisableHover] = React.useState(false);

  const compareSize = React.useCallback(() => {
    const compare = textElementRef.current ? textElementRef.current.scrollWidth > textElementRef.current.clientWidth : false;

    setDisableHover(!compare);
  }, [textElementRef, setDisableHover]);

  React.useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);
    return () => {
      window.removeEventListener('resize', compareSize);
    };
  }, []);

  return (
    <Tooltip
      arrow={true}
      placement="top-end"
      title={children}
      disableInteractive
      disableHoverListener={disableHover}
      disableTouchListener={disableHover}
      disableFocusListener={disableHover}
    >
      <EllipsedText style={style} ref={textElementRef} rowscount={rows} color={color} variant={variant} data-testid={testId || 'ellipsis'}>
        {children}
      </EllipsedText>
    </Tooltip>
  );
};

export default Ellipsis;
